import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Grid } from '@material-ui/core';
import { DateRange } from '@material-ui/pickers';

import moment from 'moment';
import { useSnackbar } from 'notistack';

import { useStyles } from './styles';

import { useApiError, useAppData, useLoading } from '../../../providers';

import {
  Header,
  PageTitle,
  QuickReportWithPercentage,
  ReportGeneralToolbarWithFilters,
  ReportTitle,
} from '../../../components';

import {
  TradingActivitiesEmailReportRequest,
  TradingActivitiesReportAggregate,
  TradingActivitiesReportData,
  TradingRequestModel,
  reportEnColumns,
} from './TradingActivitiesReportModel';

import {
  getTradingActivitiesReport,
  sendEmailTradingAvtivities,
} from './TradingActivitiesReportService';

import { ReportTable } from './components';

import { formatTradingActivityDate } from './TradingActivitiesReportUtils';

import {
  getSelectedColumnsInEn,
  getTranslatedColumnsInCorrectOrder,
} from '../../../utils';

import { trackAmplitudeData } from '../../../services/amplitudeService';

import { useCommonStyles } from '../../../styles';

import { ColumnConfig, formatNumber } from '../DataTable';
import { formatInteger, formatWithoutRounding } from '../DataTable/formatters';

export const TradingActivitiesReport: FC = () => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();

  const { enqueueSnackbar } = useSnackbar();

  const { setIsLoading } = useLoading();
  const { addError } = useApiError();

  const { translations, userData } = useAppData();

  const [selectedPartnerships, setSelectedPartnerships] = useState<string[]>(['All']);
  const [selectedLogins, setSelectedLogins] = useState<string[]>(['All']);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange<Date>>([moment().subtract(1, 'months').toDate(), moment().subtract(1, 'days').toDate()]);
  const [showReport, setShowReport] = useState(false);
  const [reportData, setReportData] = useState<TradingActivitiesReportData[]>([]);
  const [aggregate, setAggregate] = useState<TradingActivitiesReportAggregate | undefined>(undefined);

  const [translation, setTranslation] = useState<any>({});
  const [userEmail, setUserEmail] = useState<string>();

  useEffect(() => {
    if (!translations) { setTranslation({}); return; }
    try { setTranslation(JSON.parse(translations)); } catch { setTranslation({}); }
  }, [translations]);

  useEffect(() => {
    if (!userData) { setUserEmail(undefined); return; }
    try { const user = JSON.parse(userData); setUserEmail(user?.userEmail); } catch { setUserEmail(undefined); }
  }, [userData]);

  const columns = useMemo(
    (): ColumnConfig<TradingActivitiesReportData>[] => [
      { key: 'order', label: translation.text_6558 || 'Order', type: 'number', format: (v) => (v != null ? String(v) : '-') },
      { key: 'login', label: translation.text_6557 || 'Login', type: 'number', format: (v) => formatInteger(v) },
      { key: 'partnershipCode', label: translation.text_6556 || 'Partnership', type: 'string' },
      { key: 'openTime', label: translation.text_6559 || 'Open Time', type: 'date', format: (v) => formatTradingActivityDate(v) },
      { key: 'type', label: translation.text_6560 || 'Type', type: 'string' },
      { key: 'symbol', label: translation.text_6561 || 'Symbol', type: 'string' },
      { key: 'lot', label: translation.trading_activities_lot || 'Lot', type: 'number', format: (v) => Number(v ?? 0).toFixed(2) },
      { key: 'volumeUSD', label: translation.volume_usd || 'Volume (USD)', type: 'number', format: (v) => formatWithoutRounding(v) },
      { key: 'openPrice', label: translation.text_6563 || 'Open Price', type: 'number', format: (v) => formatWithoutRounding(v) },
      { key: 'closeTime', label: translation.text_6564 || 'Close Time', type: 'date', format: (v) => formatTradingActivityDate(v) },
      { key: 'closePrice', label: translation.text_6565 || 'Close Price', type: 'number', format: (v) => formatWithoutRounding(v) },
      { key: 'sl', label: translation.text_6566 || 'SL', type: 'number', format: (v) => formatNumber(v) },
      { key: 'tp', label: translation.text_6567 || 'TP', type: 'number', format: (v) => formatNumber(v) },
      { key: 'commissionUSD', label: translation.text_6568 || 'Commission USD', type: 'number', format: (v) => formatNumber(v) },
      { key: 'swapUSD', label: translation.text_6569 || 'Swap USD', type: 'number', format: (v) => formatNumber(v) },
      { key: 'profitUSD', label: translation.text_6570 || 'Profit USD', type: 'number', format: (v) => formatNumber(v) },
    ],
    [translation],
  );

  // Derived string[] needed only for email utilities
  const columnNames = useMemo(() => columns.map((col) => col.label), [columns]);

  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  useEffect(() => {
    if (columnNames.length === 16) {
      setSelectedColumns(columnNames.slice(0, 10));
    }
  }, [columnNames]);

  // Builds the totals row for export from BE aggregate data
  const totalsRowBuilder = useCallback(
    (visibleCols: ColumnConfig[]): string[] => {
      if (!aggregate) return [];

      return visibleCols.map((col, i) => {
        if (i === 0) return translation.total || 'Total';
        if (col.key === 'lot') return Number(aggregate.totalLot ?? 0).toFixed(2);
        if (col.key === 'volumeUSD') return formatWithoutRounding(aggregate.totalVolumeUSD);
        if (col.key === 'commissionUSD') return formatWithoutRounding(aggregate.totalCommissionUSD);
        if (col.key === 'profitUSD') return formatWithoutRounding(aggregate.totalProfitUSD);
        return '';
      });
    },
    [aggregate, translation.total],
  );

  const totals = useMemo(
    () => ({
      volumeUSD: aggregate?.totalVolumeUSD ?? 0,
      commissionUSD: aggregate?.totalCommissionUSD ?? 0,
      profitUSD: aggregate?.totalProfitUSD ?? 0,
      volumeUSDPercentage: aggregate?.totalVolumeUSDPercentage ?? 0,
      commissionUSDPercentage: aggregate?.totalCommissionUSDPercentage ?? 0,
      profitUSDPercentage: aggregate?.totalProfitUSDPercentage ?? 0,
    }),
    [aggregate],
  );

  const buildReportRequest = useCallback(
    (
      partnerships: string[],
      logins: string[],
      pageNumber: number,
    ): TradingRequestModel => ({
      partnershipCodes: partnerships.includes('All') ? [] : partnerships,
      login: logins.includes('All') ? [] : logins,
      fromDate: moment(selectedDateRange[0]).format('YYYY-MM-DD'),
      toDate: moment(selectedDateRange[1]).format('YYYY-MM-DD'),
      pageNumber,
    }),
    [selectedDateRange],
  );

  const getReportData = useCallback(
    async (
      partnerships: string[],
      logins: string[],
      pageNumber: number,
    ) => {
      setIsLoading(true);

      try {
        trackAmplitudeData('partnerPortal_action_report', { type: 'Requested', report_name: 'Trading Activities Report' });

        const request = buildReportRequest(partnerships, logins, pageNumber);
        const response = await getTradingActivitiesReport(request);

        setReportData(Array.isArray(response?.data?.items) ? response.data.items : []);
        setAggregate(response?.data?.aggregate ?? undefined);
        setShowReport(true);
      } catch (error: any) {
        addError(JSON.stringify(error?.response || error));
      } finally {
        setIsLoading(false);
      }
    },
    [addError, buildReportRequest, setIsLoading],
  );

  const selectPartnershipChangeHandler = useCallback(
    async (options: string[]) => {
      setSelectedPartnerships(options);
      await getReportData(options, selectedLogins, 1);
    },
    [getReportData, selectedLogins],
  );

  const selectLoginChangeHandler = useCallback(
    async (options: string[]) => {
      setSelectedLogins(options);
      await getReportData(selectedPartnerships, options, 1);
    },
    [getReportData, selectedPartnerships],
  );

  const pageChangeHandler = useCallback(
    async (pageNumber: number) => {
      await getReportData(selectedPartnerships, selectedLogins, pageNumber);
    },
    [getReportData, selectedLogins, selectedPartnerships],
  );

  const handleNewReportRequest = useCallback(async () => {
    await getReportData(selectedPartnerships, selectedLogins, 1);
  }, [getReportData, selectedLogins, selectedPartnerships]);

  const buildTradingActivitiesEmailReportRequest = useCallback(
    (): TradingActivitiesEmailReportRequest => ({
      reportPartnershipClientsTradingRequest: buildReportRequest(selectedPartnerships, selectedLogins, 1),
      reportEmailRequest: {
        email: null,
        includeTotal: true,
        tableColumns: getSelectedColumnsInEn(reportEnColumns, selectedColumns, columnNames),
        translatedTableColumns: getTranslatedColumnsInCorrectOrder(selectedColumns, columnNames),
        startDate: moment(selectedDateRange[0]).format('YYYY-MM-DD'),
        endDate: moment(selectedDateRange[1]).format('YYYY-MM-DD'),
      },
    }),
    [
      buildReportRequest,
      columnNames,
      selectedColumns,
      selectedDateRange,
      selectedLogins,
      selectedPartnerships,
    ],
  );

  const sendEmail = useCallback(async () => {
    const request = buildTradingActivitiesEmailReportRequest();

    trackAmplitudeData('partnerPortal_action_report', { type: 'SendByEmail', report_name: 'Trading Activities Report' });

    setIsLoading(true);

    try {
      await sendEmailTradingAvtivities(request);

      enqueueSnackbar(`${translation.text_1640 || 'Report sent to'} ${userEmail || ''}.`, { variant: 'success' });
    } catch (error: any) {
      addError(JSON.stringify(error?.response || error));
    } finally {
      setIsLoading(false);
    }
  }, [
    addError,
    buildTradingActivitiesEmailReportRequest,
    enqueueSnackbar,
    setIsLoading,
    translation.text_1640,
    userEmail,
  ]);

  return (
    <>
      <Header title={translation.text_6663} />

      <Grid container direction="column" className={classes.root}>
        <PageTitle title={translation.text_6663 || 'Trading Activities'} />

        <Grid container direction="column" className={commonClasses.mainContainer}>
          <ReportTitle
            translation={translation}
            title={translation.text_6497 || 'Trading Activities Report'}
            subTitle={translation.trading_activity_tooltip || ''}
            exportFileName={translation.text_6497 || 'Trading Activities Report'}
            columns={columns}
            allData={reportData}
            selectedColumns={selectedColumns}
            totalsRowBuilder={aggregate ? totalsRowBuilder : undefined}
            content={
              <ReportTable
                rows={reportData}
                aggregate={aggregate}
                columns={columns}
                translation={translation}
                selectedColumns={selectedColumns}
                onSelectColumns={setSelectedColumns}
                onPageChange={pageChangeHandler}
              />
            }
            printColumnNumber={selectedColumns.length}
            enableButtons={showReport}
            sendEmail={sendEmail}
            reportNameInEn="Trading Activities Report"
          />

          <Grid container direction="column">
            <ReportGeneralToolbarWithFilters
              translation={translation}
              dateRange={selectedDateRange}
              handleDateRangeChange={(dateRange) => {
                setSelectedDateRange(dateRange);
                setShowReport(false);
              }}
              handlePartnershipsChange={selectPartnershipChangeHandler}
              handleButtonClick={handleNewReportRequest}
              handleLoginsChange={selectLoginChangeHandler}
              applyFilters={async (partnerships, logins) => {
                setSelectedPartnerships(partnerships);
                setSelectedLogins(logins);
                await getReportData(partnerships, logins, 1);
              }}
            />

            <Grid item xs={12}>
              <Grid container direction="row" spacing={2}>
                <Grid item xs={12} sm={2}>
                  <QuickReportWithPercentage
                    title={translation.volume_usd || 'Volume (USD)'}
                    value={totals.volumeUSD}
                    percentage={totals.volumeUSDPercentage}
                  />
                </Grid>

                <Grid item xs={12} sm={2}>
                  <QuickReportWithPercentage
                    title={translation.text_6568 || 'Commission USD'}
                    value={totals.commissionUSD}
                    percentage={totals.commissionUSDPercentage}
                  />
                </Grid>

                <Grid item xs={12} sm={2}>
                  <QuickReportWithPercentage
                    title={translation.text_6570 || 'Profit USD'}
                    value={totals.profitUSD}
                    percentage={totals.profitUSDPercentage}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} className={classes.margin}>
              <ReportTable
                rows={reportData}
                aggregate={aggregate}
                columns={columns}
                translation={translation}
                selectedColumns={selectedColumns}
                onSelectColumns={setSelectedColumns}
                onPageChange={pageChangeHandler}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
